<template>
  <div class="adtech-player">
    <div class="player">
      <div class="columns is-mobile">
        <div class="column is-full">
          <span
            v-show="!isExpanded"
            data-amplitude-song-info="artist"
            data-amplitude-song-index="0"
            class="podcast-name has-text-weight-light is-size-7 is-hidden-mobile"
          />
          <div class="is-flex is-justify-content-space-between is-align-items-center">
            <div class="episode-title-container">
              <div
                v-show="!isExpanded"
                ref="episodeTitle"
                data-amplitude-song-info="name"
                data-amplitude-song-index="0"
                class="episode-title is-size-6"
                :class="{ scrolling: isScrolling }"
                :style="{ '--full-width': fullWidth }"
              />
            </div>
          </div>
          <div
            class="description-container"
          >
            <p
              class="episode-description is-size-6-mobile-md is-size-7-mobile-xs"
              :class="{ 'expanded': isExpanded }"
            >
              {{ episodeDescription }}
            </p>
            <span
              class="icon-container is-clickable"
              @click="toggleDescription"
            >
              <chevron-down
                v-if="!isExpanded"
                :fill-color="accentColor"
              />
              <chevron-up
                v-else
                :fill-color="accentColor"
              />
            </span>
          </div>
        </div>
      </div>
      <div class="player-bottom">
        <div
          id="ln-player"
          class="progress-main"
        >
          <div class="time-progress">
            <div
              id="progress-container"
              :style="progressStyle"
            >
              <input
                type="range"
                class="amplitude-song-slider"
                data-amplitude-song-index="0"
              >
              <progress
                id="song-played-progress"
                class="amplitude-song-played-progress"
                data-amplitude-song-index="0"
              />
              <progress
                id="song-buffered-progress"
                class="amplitude-buffered-progress"
                data-amplitude-song-index="0"
                value="0"
              />
            </div>
          </div>
        </div>
        <span
          class="duration px-1"
          :class="{ 'playing': playing }"
        >
          <span
            class="amplitude-time-remaining"
            data-amplitude-song-index="0"
          />
        </span>
        <div
          id="ln-player-controls"
          class="controls"
        >
          <div
            id="shuffle"
            class="amplitude-playback-speed button is-small border-radius"
            data-amplitude-song-index="0"
          />
          <div
            id="previous"
            :class="{ 'disabled-control': !playing }"
            @click="skipBack"
          >
            <rewind />
          </div>
          <div
            class="amplitude-play-pause"
            :class="{'amplitude-playing': playing, 'play-animation': !playing}"
            data-amplitude-song-index="0"
          >
            <Play
              v-show="!playing"
              :size="48"
              :fill-color="accentColor"
              @click="play"
            />
            <Pause
              v-show="playing"
              :size="48"
              @click="pause"
            />
          </div>
          <div
            id="next"
            :class="{ 'disabled-control': !playing }"
            @click="skipForward"
          >
            <fast-forward />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Amplitude from 'amplitudejs';

import Rewind from 'vue-material-design-icons/Rewind15.vue';
import FastForward from 'vue-material-design-icons/FastForward30.vue';
import Play from 'vue-material-design-icons/PlayCircle.vue';
import Pause from 'vue-material-design-icons/Pause.vue';
import ChevronDown from 'vue-material-design-icons/ChevronDown.vue';
import ChevronUp from 'vue-material-design-icons/ChevronUp.vue';

export default {
  name: 'AmpPlayer',
  components: {
    Rewind,
    FastForward,
    Play,
    Pause,
    ChevronDown,
    ChevronUp,
  },
  props: {
    podcastName: {
      type: String,
      default: '',
    },
    episodeName: {
      type: String,
      default: '',
    },
    episodeDescription: {
      type: String,
      default: '',
    },
    episodeAudio: {
      type: String,
      default: '',
    },
    accentColor: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      playing: false,
      isExpanded: false,
      sixtySecondsTimer: null, // Timer to count 60 seconds playback
      intervalTimer: null, // Timer to count playing time
      playingTime: 0, // Total time spent listening in seconds
      over30: false,
      over60: false,
      buffered60: false,
      loadedMetadata: false,
      isScrolling: false,
      fullWidth: '100%',
    };
  },
  computed: {
    progressStyle() {
      return {
        '--accent-color': this.accentColor,
      };
    },
  },
  watch: {
    loadedMetadata(newVal) {
      if (newVal && this.buffered60) {
        this.onBothConditionsMet();
      }
    },
    buffered60(newVal) {
      if (newVal && this.loadedMetadata) {
        this.onBothConditionsMet();
      }
    },
  },
  beforeDestroy() {
    this.stopTimers();
  },
  mounted() {
    const songs = [{
      name: this.episodeName,
      artist: this.podcastName,
      url: this.episodeAudio,
      cover_art_url: this.episodeCover,
      id: 0,
    }];

    Amplitude.init({
      songs,
      callbacks: {
        progress: this.checkBufferedPercentage,
        loadeddata: this.checkBufferedPercentage,
        loadedmetadata: this.setLoadedMetadata,
        play: this.checkBufferedPercentage,
        playing: this.checkBufferedPercentage,
      },
    });
    this.$nextTick(() => {
      this.checkOverflow();
    });
  },
  methods: {
    toggleDescription() {
      this.isExpanded = !this.isExpanded;
    },
    checkOverflow() {
      const titleElement = this.$el.querySelector('.episode-title');
      if (titleElement.scrollWidth > titleElement.clientWidth) {
        this.isScrolling = true;
        this.fullWidth = `${titleElement.scrollWidth}px`;
      } else {
        this.isScrolling = false;
      }
    },
    onBothConditionsMet() {
      console.log('Both loadedMetadata and buffered60 are true!');

      window.parent.postMessage({
        type: 'GA_EVENT',
        eventAction: 'buffered_60_seconds',
        eventCategory: 'engagement',
        eventLabel: 'audio_buffered',
      }, '*');
    },
    reportPlayingTime() {
      console.log('Playing time: ', this.playingTime, 'seconds');

      // Post progress data to parent window for Google Analytics
      window.parent.postMessage({
        type: 'GA_EVENT',
        eventAction: 'audio_time_update',
        eventCategory: 'engagement',
        eventLabel: 'audio_time',
        eventValue: this.playingTime,
      }, '*'); // The target origin can be set as per security requirements
    },
    start60SecondsTimer() {
      this.sixtySecondsTimer = setTimeout(() => {
        this.playingTime += 1;
        this.reportPlayingTime();

        if (this.playingTime >= 30 && !this.over30) {
          console.log('30 seconds of audio has been played!');
          this.over30 = true;
          window.parent.postMessage({
            type: 'GA_EVENT',
            eventAction: 'audio_30_seconds',
            eventCategory: 'engagement',
            eventLabel: 'audio_play',
          }, '*');
        }

        if (this.playingTime >= 60 && !this.over60) {
          console.log('30 seconds of audio has been played!');
          this.over60 = true;
          window.parent.postMessage({
            type: 'GA_EVENT',
            eventAction: 'audio_60_seconds',
            eventCategory: 'engagement',
            eventLabel: 'audio_play',
          }, '*');
        }

        this.start60SecondsTimer();
      }, 1000);
    },
    setLoadedMetadata() {
      console.log('Loaded Audio Metadata');
      this.loadedMetadata = true;
    },
    checkBufferedPercentage() {
      if (this.buffered60) {
        return;
      }

      const bufferedPercentage = Amplitude.getBuffered();
      const duration = Amplitude.getSongDuration();

      if ((bufferedPercentage / 100) * duration >= 60) {
        // Enough data has been buffered to play 60 seconds worth of audio
        this.fireBufferedEvent();
      } else {
        setTimeout(this.checkBufferedPercentage, 500);
      }
    },
    fireBufferedEvent() {
      // Fire your event here
      console.log('60 seconds worth of audio has been buffered!');
      this.buffered60 = true;
    },
    stopTimers() {
      if (this.sixtySecondsTimer) {
        clearTimeout(this.sixtySecondsTimer);
      }
    },
    play() {
      this.playing = true;

      // Use postMessage to send the event data to the parent
      window.parent.postMessage({
        type: 'GA_EVENT',
        eventAction: 'audio_play',
        eventCategory: 'engagement',
        eventLabel: 'play_button',
      }, '*');

      this.start60SecondsTimer();
    },
    pause() {
      this.playing = false;
      this.stopTimers();
    },
    showIcon(directoryName, directoryIcon) {
      return directoryName.toLowerCase().includes(directoryIcon);
    },
    skipBack() {
      if (this.playing) {
        const played = Amplitude.getSongPlayedSeconds();
        Amplitude.skipTo(played - 15, 0);
      }
    },
    skipForward() {
      if (this.playing) {
        const played = Amplitude.getSongPlayedSeconds();
        Amplitude.skipTo(played + 30, 0);
      }
    },
    songProgress(e) {
      const offset = e.target.getBoundingClientRect();
      const x = e.pageX - offset.left;

      Amplitude.setSongPlayedPercentage(
        (parseFloat(x) / parseFloat(e.target.offsetWidth)) * 100,
      );
    },
  },
};
</script>

<style lang="scss">
.border-radius {
  border-radius: 50px !important;
  font-size: 0.6rem !important;
}

.podcast-name {
  color: #EEE;
}

.episode-title {
  white-space: nowrap;
  font-weight: 800;
  font-size: 20px;
  line-height: 30px;
  color: #EEE;
  width: 100%; /* Set a width */
}

.episode-title-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.episode-title-container::before,
.episode-title-container::after {
  content: '';
  position: absolute;
  top: 0;
  height: 100%;
  width: 8px; // Width of the fade effect
  z-index: 1; // Ensure it's above the text
}

.episode-title-container::before {
  left: 0;
  background: linear-gradient(to left, rgba(51, 51, 51, 0), rgba(51, 51, 51, 1));
}

.episode-title-container::after {
  right: 0;
  background: linear-gradient(to right, rgba(51, 51, 51, 0), rgba(51, 51, 51, 1));
}

.episode-description {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 600px;
  color: #BBB;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease; /* Smooth transition for expanding and collapsing */
}

.episode-description.expanded {
  white-space: normal;
  overflow-y: auto; /* Enable vertical scrolling */
  max-height: 60px;
  text-overflow: clip;
}

.description-container {
  display: flex;
  align-items: center;
}

.icon-container {
  display: flex;
  align-items: center;
}

.adtech-player {
  display: flex;
  justify-content: center; /* aligns the child divs horizontally in the center */
  align-items: center; /* aligns the child divs vertically in the center */
  height: 100vh; /* makes the adtech-player take up the entire viewport height */
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background-color: #333333;
}

.cover-art {
  margin: 16px 0 0 15px;
  flex:0 0 80px
}
.cover-art img {
  display: block;
  height: 80px;
  width: 80px;
  border-radius: 5px
}

.controls {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.5;
  font-size: 0px;
  color: #BBB;
  min-width: 90px;
}

.disabled-control {
  opacity: 0.5;
  cursor: not-allowed !important;
}

.columns:not(:last-child) {
    margin-bottom: 0 !important;
}

.flex-direction {
    flex-direction: row;
}

.player {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  padding-inline: 16px;
  min-width: 0;
  z-index: 1;
  background-color: #333;
}

@media all and (max-width: 300px) {
  .listen-network-player {
    width: 270px
  }

  #shuffle {
    display: none !important;
  }
}

.player-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  // Ensure that the container takes the full width if necessary
  width: 100%;
}

.progress-main {
  flex: 1 1 auto; // Flex-grow, flex-shrink and flex-basis
  min-width: 0; // This ensures that it can shrink below its content's intrinsic width
}

#ln-player-controls {
  flex-shrink: 0; // Prevents this element from shrinking
  // If needed, you can set a max-width to control its size
}

// Media query for very small screens (less than 300px)

@media (max-width: 300px) {
  .player-bottom {
    justify-content: flex-start;
  }

  .progress-main,
  #ln-player-controls {
    width: 120px;
  }
}

.rounded {
  border-radius: 6px;
}

.playing .amplitude-time-remaining::before {
  content: '-';
  margin-right: 2px;
}

/* Update the rest of the styles with a similar scheme */

/* For instance */
div#ln-player div.time-progress span.current-time {
  color: #BBB;
  font-size: 12px;
  display: block;
  float: left;
}
div#ln-player div.time-progress div#progress-container {
  position: relative;
  height: 20px;
  cursor: pointer;
  /*
      IE 11
    */
}

div#ln-player div.time-progress div#progress-container:hover input[type=range].amplitude-song-slider::-webkit-slider-thumb {
  display: block;
}

div#ln-player div.time-progress div#progress-container:hover input[type=range].amplitude-song-slider::-moz-range-thumb {
  visibility: visible;
}

div#ln-player div.time-progress div#progress-container progress#song-played-progress {
  width: 100%;
  position: absolute;
  left: 0;
  top: 8px;
  right: 0;
  z-index: 60;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 5px;
  background: transparent;
  border: none;
  /* Needed for Firefox */
}

@media all and (-ms-high-contrast: none) {
  div#ln-player div.time-progress div#progress-container *::-ms-backdrop,
  div#ln-player div.time-progress div#progress-container progress#song-played-progress {
    border: none;
    background-color: #757575;
  }
}

@supports (-ms-ime-align: auto) {
  div#ln-player div.time-progress div#progress-container progress#song-played-progress {
    border: none;
  }
}

div#ln-player div.time-progress div#progress-container progress#song-played-progress[value]::-webkit-progress-bar {
  background: none;
  border-radius: 5px;
}

div#ln-player div.time-progress div#progress-container progress#song-played-progress[value]::-webkit-progress-value {
  background-color: var(--accent-color);
  border-radius: 5px;
}

div#ln-player div.time-progress div#progress-container progress#song-played-progress::-moz-progress-bar {
  background: none;
  border-radius: 5px;
  background-color: var(--accent-color);
  height: 5px;
  margin-top: -2px;
}

div#ln-player div.time-progress div#progress-container progress#song-buffered-progress {
  position: absolute;
  left: 0;
  top: 8px;
  right: 0;
  width: 100%;
  z-index: 10;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 5px;
  background: transparent;
  border: none;
  background-color: #D7DEE3;
}

div#ln-player div.time-progress div#progress-container progress#song-buffered-progress[value]::-webkit-progress-bar {
  background-color: #757575;
  border-radius: 5px;
}

div#ln-player div.time-progress div#progress-container progress#song-buffered-progress[value]::-webkit-progress-value {
  background-color: #757575;
  border-radius: 5px;
  transition: width .1s ease;
}

div#ln-player div.time-progress div#progress-container progress#song-buffered-progress::-moz-progress-bar {
  background: none;
  border-radius: 5px;
  background-color: #757575;
  height: 5px;
  margin-top: -2px;
}

div#ln-player div.time-progress div#progress-container progress::-ms-fill {
  border: none;
}

@-moz-document url-prefix() {
  div#ln-player div.time-progress div#progress-container progress#song-buffered-progress {
    top: 9px;
    border: none;
  }
}

@media all and (-ms-high-contrast: none) {
  div#ln-player div.time-progress div#progress-container *::-ms-backdrop,
  div#ln-player div.time-progress div#progress-container progress#song-buffered-progress {
    color: #78909C;
    border: none;
  }
}

@supports (-ms-ime-align: auto) {
  div#ln-player div.time-progress div#progress-container progress#song-buffered-progress {
    color: #78909C;
    border: none;
  }
}

div#ln-player div.time-progress div#progress-container input[type=range] {
  -webkit-appearance: none;
  width: 100%;
  margin: 7.5px 0;
  position: absolute;
  z-index: 9999;
  top: -7px;
  height: 20px;
  cursor: pointer;
  background-color: inherit;
}

div#ln-player div.time-progress div#progress-container input[type=range]:focus {
  outline: none;
}

div#ln-player div.time-progress div#progress-container input[type=range]::-webkit-slider-runnable-track {
  width: 100%;
  height: 0px;
  cursor: pointer;
  background: var(--accent-color);
  border-radius: 0px;
  border: 0px solid #010101;
}

div#ln-player div.time-progress div#progress-container input[type=range]::-webkit-slider-thumb {
  border: 1px solid var(--accent-color);
  height: 15px;
  width: 15px;
  border-radius: 16px;
  background: var(--accent-color);
  cursor: pointer;
  -webkit-appearance: none;
  margin-top: -7.5px;
}

div#ln-player div.time-progress div#progress-container input[type=range]:focus::-webkit-slider-runnable-track {
  background: var(--accent-color);
}

div#ln-player div.time-progress div#progress-container input[type=range]::-moz-range-track {
  width: 100%;
  height: 0px;
  cursor: pointer;
  background: var(--accent-color);
  border-radius: 0px;
  border: 0px solid #010101;
}

div#ln-player div.time-progress div#progress-container input[type=range]::-moz-range-thumb {
  border: 1px solid var(--accent-color);
  height: 15px;
  width: 15px;
  border-radius: 16px;
  background: var(--accent-color);
  cursor: pointer;
}

div#ln-player div.time-progress div#progress-container input[type=range]::-ms-track {
  width: 100%;
  height: 0px;
  cursor: pointer;
  background: transparent;
  border-color: transparent;
  color: transparent;
}

div#ln-player div.time-progress div#progress-container input[type=range]::-ms-fill-lower {
  background: #003d57;
  border: 0px solid #010101;
  border-radius: 0px;
}

div#ln-player div.time-progress div#progress-container input[type=range]::-ms-fill-upper {
  background: var(--accent-color);
  border: 0px solid #010101;
  border-radius: 0px;
}

div#ln-player div.time-progress div#progress-container input[type=range]::-ms-thumb {
  border: 1px solid var(--accent-color);
  height: 15px;
  width: 15px;
  border-radius: 16px;
  background: var(--accent-color);
  cursor: pointer;
  display: none;
}

@media all and (-ms-high-contrast: none) {
  div#ln-player div.time-progress div#progress-container *::-ms-backdrop,
  div#ln-player div.time-progress div#progress-container input[type="range"].amplitude-song-slider {
    padding: 0px;
  }
  div#ln-player div.time-progress div#progress-container *::-ms-backdrop,
  div#ln-player div.time-progress div#progress-container input[type=range].amplitude-song-slider::-ms-thumb {
    height: 15px;
    width: 15px;
    border-radius: 10px;
    cursor: pointer;
    margin-top: -8px;
  }
  div#ln-player div.time-progress div#progress-container *::-ms-backdrop,
  div#ln-player div.time-progress div#progress-container input[type=range].amplitude-song-slider::-ms-track {
    border-width: 15px 0;
    border-color: transparent;
  }
  div#ln-player div.time-progress div#progress-container *::-ms-backdrop,
  div#ln-player div.time-progress div#progress-container input[type=range].amplitude-song-slider::-ms-fill-lower {
    background: #757575;
    border-radius: 10px;
  }
  div#ln-player div.time-progress div#progress-container *::-ms-backdrop,
  div#ln-player div.time-progress div#progress-container input[type=range].amplitude-song-slider::-ms-fill-upper {
    background: #757575;
    border-radius: 10px;
  }
}

@supports (-ms-ime-align: auto) {
  div#ln-player div.time-progress div#progress-container input[type=range].amplitude-song-slider::-ms-thumb {
    height: 15px;
    width: 15px;
    margin-top: 3px;
  }
}

div#ln-player div.time-progress div#progress-container input[type=range]:focus::-ms-fill-lower {
  background: var(--accent-color)
}

div#ln-player div.time-progress div#progress-container input[type=range]:focus::-ms-fill-upper {
  background: var(--accent-color);
}

@media all and (min-width: 300px) {
  .duration {
    margin-right: 14px;
  }
}

.duration {
  color: #757575;
  font-size: 12px;
}

div#ln-player-controls div#shuffle {
  display: inline-block;
  cursor: pointer;
}

div#ln-player-controls div#shuffle.amplitude-playback-speed-10:before {
  content: "1x";
  color: #757575;
}

div#ln-player-controls div#shuffle.amplitude-playback-speed-15:before {
  content: "1.5x";
  color: #757575;
}

div#ln-player-controls div#shuffle.amplitude-playback-speed-20:before {
  content: "2x";
  color: #757575;
}

div#ln-player-controls div#previous {
  display: inline-block;
  cursor: pointer;
  vertical-align: middle;
  margin-left: 5px;
}

.play-animation {
  display: inline-block;
  cursor: pointer;
  vertical-align: middle;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
}

.play-animation {
  animation: pulse 1.5s infinite;
}

div#ln-player-controls div#next {
  display: inline-block;
  cursor: pointer;
}

div#ln-player-controls div#repeat {
  display: inline-block;
  cursor: pointer;
  vertical-align: middle;
}

div#ln-player-controls div#repeat a {
  color: #2F312D;
}

@keyframes scrollEffect {
  0%, 15% {
    transform: translateX(0);
  }
  90%, 95% {
    transform: translateX(calc(-1 * var(--full-width) + 100%));
  }
  96%, 100% {
    transform: translateX(0);
  }
}

.scrolling {
  display: inline-block;
  animation: scrollEffect 14s linear infinite; /* Duration for slower scroll */
}
</style>
